// shell/components/field-group.ts
// Standalone field group (card) and form row components.

import { createElement } from '../utils/dom';

// ============================================================================
// Field Group (Card)
// ============================================================================

/**
 * Configuration for createFieldGroup.
 */
export interface FieldGroupConfig {
    /** Group title displayed in the header */
    title: string;
    /** Whether the group can be collapsed (default: false) */
    collapsible?: boolean;
    /** Whether the group starts collapsed (only when collapsible, default: false) */
    collapsed?: boolean;
    /** Label column width: 's' (80px), 'm' (120px), 'l' (160px). Default: 's'. */
    labelSize?: 's' | 'm' | 'l';
    /** Extra CSS class on the outer element */
    className?: string;
}

/**
 * A field group (card) container with a title header and body area.
 */
export interface FieldGroup {
    /** The outer container element */
    element: HTMLElement;
    /** The body area where content is appended */
    body: HTMLElement;
    /** Programmatically set collapsed state (only effective when collapsible) */
    setCollapsed(collapsed: boolean): void;
    /** Clean up */
    dispose(): void;
}

/**
 * Creates a field group — a bordered card with a title header and body area.
 * Optionally collapsible. Reuses el-section styles from entity-layout.
 * When title is empty, the header is omitted entirely (body-only container).
 */
export function createFieldGroup(config: FieldGroupConfig): FieldGroup {
    const hasTitle = !!config.title;
    const collapsible = hasTitle && (config.collapsible ?? false);
    const collapsed = collapsible && (config.collapsed ?? false);

    const labelSize = config.labelSize;
    const classNames = [
        'el-section',
        collapsible ? '' : 'el-section--static',
        collapsed ? 'el-section--collapsed' : '',
        labelSize ? `el-section--labels-${labelSize}` : '',
        config.className ?? '',
    ].filter(Boolean).join(' ');

    const el = createElement('div', { className: classNames });

    // Header / toggle — only when title is provided
    let toggle: HTMLElement | null = null;
    if (hasTitle) {
        toggle = createElement(collapsible ? 'button' : 'div', {
            className: 'el-section-toggle',
            attributes: collapsible ? {
                type: 'button',
                'aria-expanded': collapsed ? 'false' : 'true',
            } : {},
        });

        if (collapsible) {
            const arrow = createElement('span', {
                className: 'el-section-arrow',
                textContent: '\u25B6', // ▶
            });
            toggle.appendChild(arrow);
        }

        const text = createElement('span', {
            className: 'el-section-text',
            textContent: config.title,
        });
        toggle.appendChild(text);
        el.appendChild(toggle);

        // Toggle click handler
        if (collapsible) {
            toggle.addEventListener('click', () => {
                const isCollapsed = el.classList.toggle('el-section--collapsed');
                toggle!.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
            });
        }
    }

    // Body
    const body = createElement('div', {
        className: 'el-section-body',
    });
    el.appendChild(body);

    return {
        element: el,
        body,
        setCollapsed(value: boolean) {
            if (!collapsible || !toggle) return;
            el.classList.toggle('el-section--collapsed', value);
            toggle.setAttribute('aria-expanded', value ? 'false' : 'true');
        },
        dispose() {
            el.remove();
        },
    };
}

// ============================================================================
// Form Row
// ============================================================================

/**
 * Configuration for createFormRow.
 */
export interface FormRowConfig {
    /** Label text */
    label: string;
    /** Optional id for the row (for show/hide lookups) */
    id?: string;
}

/**
 * A form row with a label and content area.
 */
export interface FormRow {
    /** The row element */
    element: HTMLElement;
    /** The content area where the control goes */
    content: HTMLElement;
    /** The label element */
    label: HTMLElement;
    /** Show or hide the row */
    setVisible(visible: boolean): void;
}

/**
 * Creates a horizontal form row with a label on the left and content area on the right.
 */
export function createFormRow(config: FormRowConfig): FormRow {
    const el = createElement('div', {
        className: 'el-form-row',
        attributes: config.id ? { 'data-row-id': config.id } : {},
    });

    const label = createElement('span', {
        className: 'el-form-label',
        textContent: config.label,
    });
    el.appendChild(label);

    const content = createElement('div', {
        className: 'el-form-content',
    });
    el.appendChild(content);

    return {
        element: el,
        content,
        label,
        setVisible(visible: boolean) {
            el.style.display = visible ? '' : 'none';
        },
    };
}
